import { useState, useCallback, useEffect } from 'react';
import { playHover, playClick } from './audio';
import { CitySearch, saveCachedCityMetadata } from './components/screens/CitySearch';
import { LoadingScreen } from './components/screens/LoadingScreen';
import { GameScreen } from './components/screens/GameScreen';
import { Scanlines } from './components/shared/Scanlines';
import { GeocodingResult, computeBounds } from './pipeline/overpass';
import { loadCityData } from './pipeline/cityLoader';
import { CityData } from './types/map';
import { MainMenu } from './components/screens/MainMenu';
import { DeckDraftScreen } from './components/screens/DeckDraftScreen';
import { HQScreen } from './components/screens/HQScreen';
import { PassiveDraftScreen } from './components/screens/PassiveDraftScreen';
import { KillSummaryScreen } from './components/screens/KillSummaryScreen';
import { RunMapScreen } from './components/screens/RunMapScreen';
import { ExtractionRewardScreen } from './components/screens/ExtractionRewardScreen';
import { tuning } from './data/tuning';
import { CardsReferenceScreen } from './components/screens/CardsReferenceScreen';
import { PassivesReferenceScreen } from './components/screens/PassivesReferenceScreen';
import { AgentsReferenceScreen } from './components/screens/AgentsReferenceScreen';
import { StyleguideScreen } from './components/screens/StyleguideScreen';
import { TutorialScreen } from './components/screens/TutorialScreen';
import { BuildSelectScreen } from './components/screens/BuildSelectScreen';
import { MissionBriefingScreen } from './components/screens/MissionBriefingScreen';
import { CURATED_CITIES } from './data/cities';
import { PlayerBuild } from './data/builds';
import { createCard } from './data/cards';
import { RunState } from './types/run';
import { RoundResult, Card, Item } from './types/game';
import { GameState } from './types/game';
import {
  createRunState,
  loadRunState,
  saveRunState,
  deleteRunState,
  applyRoundEnd,
  prepareNextRound,
  saveMidRound,
  clearMidRoundSave,
  addPassive,
  getPassiveDraftOptions,
  advanceToNode,
  markCurrentNodeCompleted,
} from './engine/runState';
import { getNode } from './engine/runMap';
import { PASSIVE_TEMPLATES, PassiveTemplate } from './data/passives';

type AppState =
  | { screen: 'menu' }
  | { screen: 'build-select' }
  | { screen: 'deck-draft' }
  | { screen: 'practice-search' }
  | { screen: 'loading'; cityName: string; stage: string; progress: number; runState: RunState | null }
  // Mission briefing — shown after city load finishes, before the round mounts.
  | { screen: 'mission-briefing'; cityData: CityData; runState: RunState | null }
  | { screen: 'game'; cityData: CityData; runState: RunState | null }
  // Run-start passive draft, played immediately after deck draft before the map shows
  | { screen: 'passive-draft-start'; runState: RunState; options: PassiveTemplate[] }
  // Per-round passive draft, played on kill rounds before returning to the map
  | { screen: 'passive-draft-round'; runState: RunState; roundResult: RoundResult; options: PassiveTemplate[] }
  // Kill summary — shown immediately after a kill-round ends.
  | {
      screen: 'kill-summary';
      runState: RunState;
      killMarks: number;
      passphraseMarks: number;
      collectedItems: Item[];
      passiveOptions: PassiveTemplate[];
      round: number;
    }
  // Run path map — the player's view of the run progression.
  | { screen: 'run-map'; runState: RunState }
  // Post-extraction card draft reward (extraction-op nodes only).
  | { screen: 'extraction-reward'; runState: RunState }
  | { screen: 'hq'; runState: RunState; shopEnabled: boolean }
  // Reference pages (menu-only, no game state)
  | { screen: 'cards-reference' }
  | { screen: 'passives-reference' }
  | { screen: 'agents-reference' }
  | { screen: 'styleguide' }
  | { screen: 'tutorial' };

function App() {
  const [state, setState] = useState<AppState>({ screen: 'menu' });
  const [savedRun, setSavedRun] = useState<RunState | null>(() => loadRunState());

  // Global UI sounds — covers all screens
  useEffect(() => {
    const interactiveSelector = 'button, .hand-card, .inventory-slot-wrapper, .status-chip, .end-turn-button, .draft-card-option, .run-map-node--clickable, .run-map-button';
    let lastHoverTarget: EventTarget | null = null;
    const onMouseOver = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.(interactiveSelector);
      if (target && target !== lastHoverTarget) { lastHoverTarget = target; playHover(); }
    };
    const onMouseOut = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.(interactiveSelector);
      if (!target) lastHoverTarget = null;
    };
    const onClick = (e: MouseEvent) => {
      const target = (e.target as HTMLElement).closest?.(interactiveSelector);
      if (target) playClick();
    };
    document.addEventListener('mouseover', onMouseOver, true);
    document.addEventListener('mouseout', onMouseOut, true);
    document.addEventListener('click', onClick, true);
    return () => {
      document.removeEventListener('mouseover', onMouseOver, true);
      document.removeEventListener('mouseout', onMouseOut, true);
      document.removeEventListener('click', onClick, true);
    };
  }, []);

  // --- City loading pipeline (shared by run mode and practice mode) ---
  const loadCity = useCallback(async (
    cityName: string,
    lat: number,
    lng: number,
    bounds: { south: number; west: number; north: number; east: number } | undefined,
    runState: RunState | null,
    geocodingResult?: GeocodingResult,
  ) => {
    setState({
      screen: 'loading',
      cityName,
      stage: 'Initializing...',
      progress: 0,
      runState,
    });

    try {
      const center = { lat, lng };
      const computedBounds = computeBounds(center, bounds);

      const cityData = await loadCityData(cityName, center, bounds, (stage, progress) => {
        setState({
          screen: 'loading',
          cityName,
          stage,
          progress,
          runState,
        });
      });

      if (geocodingResult) {
        saveCachedCityMetadata(geocodingResult, computedBounds);
      }

      setState({ screen: 'mission-briefing', cityData, runState });
    } catch (err) {
      console.error('Failed to load city:', err);
      // Run-mode failure: bounce to the map so the player can pick a different
      // node rather than back to the menu.
      if (runState) {
        setState({ screen: 'run-map', runState });
      } else {
        setState({ screen: 'practice-search' });
      }
      const msg = err instanceof Error ? err.message : 'Unknown error';
      alert(`Failed to load ${cityName}.\n\n${msg}\n\nThe Overpass API may be busy or the city too dense — try again in a moment or try a smaller city.`);
    }
  }, []);

  // --- Run-map node progression ---

  /** Given a run whose currentNodeId has just been advanced, route to the
   * appropriate next screen based on the node type. */
  const enterCurrentNode = useCallback((run: RunState) => {
    if (!run.runMap || !run.currentNodeId) {
      setState({ screen: 'run-map', runState: run });
      return;
    }
    const node = getNode(run.runMap, run.currentNodeId);
    if (!node) {
      setState({ screen: 'run-map', runState: run });
      return;
    }
    // HQ node: no round, just open the shop. Shop exit will mark the node
    // completed and route back to the map.
    if (node.type === 'hq') {
      saveRunState(run);
      setSavedRun(run);
      setState({ screen: 'hq', runState: run, shopEnabled: true });
      return;
    }
    // City round (hit / elite / extraction / boss — all treated as Hit in Ship 1).
    const city = CURATED_CITIES.find(c => c.name === node.cityName);
    if (!city) {
      console.error('Node has no valid city:', node);
      setState({ screen: 'run-map', runState: run });
      return;
    }
    const preparedRun = prepareNextRound(run, city.name);
    saveRunState(preparedRun);
    setSavedRun(preparedRun);
    // Pass city.name (not displayName) so the R2 slug matches the precomputed bucket.
    loadCity(city.name, city.lat, city.lng, undefined, preparedRun);
  }, [loadCity]);

  const handleMapNodeSelected = useCallback((nodeId: string) => {
    if (state.screen !== 'run-map') return;
    const advanced = advanceToNode(state.runState, nodeId);
    if (advanced === state.runState) return; // node wasn't reachable
    enterCurrentNode(advanced);
  }, [state, enterCurrentNode]);

  // --- Flow handlers ---

  const handleNewRun = useCallback(() => {
    deleteRunState();
    setState({ screen: 'build-select' });
  }, []);

  const handleContinueRun = useCallback(() => {
    const run = loadRunState();
    if (!run) return;

    // Mid-round save takes precedence — finish the round they were in.
    if (run.midRoundSave) {
      const { cityName, cityLat, cityLng } = run.midRoundSave;
      loadCity(cityName, cityLat, cityLng, undefined, run);
      return;
    }

    // If the player was sitting on a between-round reward screen when they
    // closed the tab / refreshed / hit a black-screen bug, resume there
    // rather than skipping past it to the map. Random rolls (passive
    // options, extraction-reward cards) re-roll on resume — players
    // couldn't predict the original roll either, so this is honest enough.
    const pr = run.pendingReward;
    if (pr) {
      setSavedRun(run);
      if (pr.phase === 'kill-summary') {
        const options = getPassiveDraftOptions(run, false);
        setState({
          screen: 'kill-summary',
          runState: run,
          killMarks: pr.killMarks ?? 0,
          passphraseMarks: pr.passphraseMarks ?? 0,
          collectedItems: pr.collectedItemsSnapshot ?? [],
          passiveOptions: options,
          round: pr.roundSnapshot ?? run.round,
        });
        return;
      }
      if (pr.phase === 'passive-draft-round') {
        const options = getPassiveDraftOptions(run, false);
        if (options.length === 0) {
          // Pool exhausted (player owns everything now). Clear pending and
          // fall through to map rather than rendering an empty draft.
          const cleared: RunState = { ...run, pendingReward: null };
          saveRunState(cleared);
          setSavedRun(cleared);
          setState({ screen: 'run-map', runState: cleared });
          return;
        }
        setState({
          screen: 'passive-draft-round',
          runState: run,
          roundResult: 'kill',
          options,
        });
        return;
      }
      if (pr.phase === 'extraction-reward') {
        setState({ screen: 'extraction-reward', runState: run });
        return;
      }
    }

    // Otherwise drop them on the map.
    setSavedRun(run);
    setState({ screen: 'run-map', runState: run });
  }, [loadCity]);

  /** Called when player finishes drafting their starting deck */
  const handleDeckDraftComplete = useCallback((draftedCards: Card[]) => {
    if (state.screen !== 'deck-draft') return;

    // Create run (including map) with the drafted deck.
    const run = createRunState();
    run.deck = draftedCards;
    saveRunState(run);
    setSavedRun(run);

    // Run-start passive draft before the map shows.
    const options = getPassiveDraftOptions(run, true);
    setState({
      screen: 'passive-draft-start',
      runState: run,
      options,
    });
  }, [state]);

  /** Called when player selects a build (or freestyle) on the build selection screen */
  const handleBuildSelect = useCallback((selection: PlayerBuild | 'freestyle') => {
    if (state.screen !== 'build-select') return;

    if (selection === 'freestyle') {
      setState({ screen: 'deck-draft' });
      return;
    }

    // Preset build: create run with the build's deck + passive.
    const run = createRunState();
    run.deck = selection.cardKeys.map(k => createCard(k));
    const withPassive = addPassive(run, selection.passiveKey);
    saveRunState(withPassive);
    setSavedRun(withPassive);

    // Preset builds skip the passive draft — go straight to the map.
    setState({ screen: 'run-map', runState: withPassive });
  }, [state]);

  /** Called when player finishes the run-start passive draft */
  const handleStartPassivePicked = useCallback((passiveKey: string) => {
    if (state.screen !== 'passive-draft-start') return;
    const { runState } = state;
    const updatedRun = addPassive(runState, passiveKey);
    saveRunState(updatedRun);
    setSavedRun(updatedRun);

    setState({ screen: 'run-map', runState: updatedRun });
  }, [state]);

  const handlePractice = useCallback(() => {
    setState({ screen: 'practice-search' });
  }, []);

  /** CitySearch callback for practice mode */
  const handlePracticeCitySelected = useCallback(async (result: GeocodingResult) => {
    loadCity(result.name, result.lat, result.lng, result.bounds, null, result);
  }, [loadCity]);

  /** Called from GameScreen when a round ends (kill, escape, or death) */
  const handleRoundComplete = useCallback((
    result: RoundResult,
    earnedMarks: number,
    collectedItems: Item[],
    playerDeck: Card[],
    safehouseCards?: Card[],
    agentCodename?: string,
  ) => {
    if (state.screen !== 'game' || !state.runState) {
      // Practice mode — back to menu.
      setState({ screen: 'menu' });
      return;
    }

    // Ship 2: safehouse-card retention is gated by a ≥ 2 passphrase count
    // at the GameScreen level — by the time we reach here, `playerDeck`
    // already has the safehouse cards stripped if the player didn't
    // collect enough passphrases. Nothing to re-filter here.
    const finalDeck = playerDeck;

    let run = clearMidRoundSave({ ...state.runState, deck: finalDeck });
    run = applyRoundEnd(run, result, earnedMarks, collectedItems);
    if (agentCodename) {
      const existing = run.usedCodenames ?? [];
      if (!existing.includes(agentCodename)) {
        run = { ...run, usedCodenames: [...existing, agentCodename] };
      }
    }
    if (safehouseCards && safehouseCards.length > 0) {
      run = { ...run, safehouseCardsThisRound: safehouseCards };
    }

    if (result === 'death') {
      deleteRunState();
      setSavedRun(null);
      setState({ screen: 'menu' });
      return;
    }

    // Mark the current node as completed (the player cleared the round).
    run = markCurrentNodeCompleted(run);
    saveRunState(run);
    setSavedRun(run);

    // Was this the boss node? If yes, run is won.
    const currentNode = run.runMap && run.currentNodeId
      ? getNode(run.runMap, run.currentNodeId)
      : null;
    if (currentNode?.type === 'boss' && result === 'kill') {
      // Victory! For Ship 1 we just show a simple message and end the run.
      alert('MISSION COMPLETE — The target has been eliminated. Run archived.');
      deleteRunState();
      setSavedRun(null);
      setState({ screen: 'menu' });
      return;
    }

    // Kill → show summary, then passive draft, then map.
    // Hit/Elite/Boss nodes reward a passive on kill; extraction nodes do
    // NOT (their reward is the card draft, awarded on extract). Extraction
    // kills just register as a combat clear — no passive, no summary.
    const killGrantsPassive = currentNode?.type !== 'extraction';
    if (result === 'kill' && killGrantsPassive) {
      const options = getPassiveDraftOptions(run, false);
      const killBase = tuning.marks.killReward;
      const passphraseReward = Math.max(0, earnedMarks - killBase);
      // Persist pending-reward snapshot so a refresh + continue resumes
      // here instead of skipping the reward.
      const runWithPending: RunState = {
        ...run,
        pendingReward: {
          phase: 'kill-summary',
          killMarks: killBase,
          passphraseMarks: passphraseReward,
          collectedItemsSnapshot: collectedItems,
          roundSnapshot: run.round,
        },
      };
      saveRunState(runWithPending);
      setSavedRun(runWithPending);
      setState({
        screen: 'kill-summary',
        runState: runWithPending,
        killMarks: killBase,
        passphraseMarks: passphraseReward,
        collectedItems,
        passiveOptions: options,
        round: run.round,
      });
      return;
    }

    // Successful extraction-op escape → card-draft reward screen. Extraction
    // ops are the only place 'escape' can fire in run mode today.
    if (result === 'escape' && currentNode?.type === 'extraction') {
      const runWithPending: RunState = {
        ...run,
        pendingReward: { phase: 'extraction-reward' },
      };
      saveRunState(runWithPending);
      setSavedRun(runWithPending);
      setState({ screen: 'extraction-reward', runState: runWithPending });
      return;
    }

    // Fallback: any branch we didn't explicitly handle goes back to the map.
    // Logging here so if a future scenario silently lands here we have a
    // breadcrumb instead of a confused player. (Previously, an unhandled
    // result/node combination would still render the map fine, but the
    // player loses a reward they were expecting — so flag it loudly.)
    // eslint-disable-next-line no-console
    console.warn(
      `[handleRoundComplete] Unhandled result/node combination — falling back to map. ` +
      `result=${result}, nodeType=${currentNode?.type ?? 'null'}, nodeId=${run.currentNodeId ?? 'null'}`,
    );
    setState({ screen: 'run-map', runState: run });
  }, [state]);

  /** Called from ExtractionRewardScreen — player picked a card. */
  const handleExtractionRewardPicked = useCallback((card: Card) => {
    if (state.screen !== 'extraction-reward') return;
    // Add card to deck AND clear pendingReward so a refresh from the map
    // doesn't bounce back to the (already-claimed) reward screen.
    const run: RunState = {
      ...state.runState,
      deck: [...state.runState.deck, card],
      pendingReward: null,
    };
    saveRunState(run);
    setSavedRun(run);
    setState({ screen: 'run-map', runState: run });
  }, [state]);

  /** Called from ExtractionRewardScreen — player skipped the reward. */
  const handleExtractionRewardSkipped = useCallback(() => {
    if (state.screen !== 'extraction-reward') return;
    const cleared: RunState = { ...state.runState, pendingReward: null };
    saveRunState(cleared);
    setSavedRun(cleared);
    setState({ screen: 'run-map', runState: cleared });
  }, [state]);

  /** Called when player continues from the kill summary screen. */
  const handleKillSummaryContinue = useCallback(() => {
    if (state.screen !== 'kill-summary') return;
    const { runState: run, passiveOptions } = state;
    if (passiveOptions.length > 0) {
      // Advance the pending-reward phase so refresh+continue re-enters
      // the passive draft (not the kill summary again).
      const runPending: RunState = {
        ...run,
        pendingReward: { phase: 'passive-draft-round' },
      };
      saveRunState(runPending);
      setSavedRun(runPending);
      setState({ screen: 'passive-draft-round', runState: runPending, roundResult: 'kill', options: passiveOptions });
      return;
    }
    // Pool exhausted — skip passive draft and go straight to the map.
    // Clear pending reward since there's nothing left to claim.
    const cleared: RunState = { ...run, pendingReward: null };
    saveRunState(cleared);
    setSavedRun(cleared);
    setState({ screen: 'run-map', runState: cleared });
  }, [state]);

  /** Called when player finishes the per-round passive draft (kill only) */
  const handleRoundPassivePicked = useCallback((passiveKey: string) => {
    if (state.screen !== 'passive-draft-round') return;
    // Reward consumed — clear the pending-reward marker before saving.
    const updatedRun: RunState = {
      ...addPassive(state.runState, passiveKey),
      pendingReward: null,
    };
    saveRunState(updatedRun);
    setSavedRun(updatedRun);
    setState({ screen: 'run-map', runState: updatedRun });
  }, [state]);

  /** Called from HQScreen deploy button — in Ship 1, HQ is a map node, so
   * "deploy" means "finish shopping and go back to the map". */
  const handleDeploy = useCallback((updatedRun: RunState) => {
    const completed = markCurrentNodeCompleted(updatedRun);
    saveRunState(completed);
    setSavedRun(completed);
    setState({ screen: 'run-map', runState: completed });
  }, []);

  /** Called from HQScreen end run button — or from RunMapScreen. */
  const handleEndRun = useCallback(() => {
    deleteRunState();
    setSavedRun(null);
    setState({ screen: 'menu' });
  }, []);

  /** Save mid-round and exit to menu */
  const handleSaveAndExit = useCallback((gameState: GameState) => {
    if (state.screen !== 'game' || !state.runState) return;
    const cityName = state.cityData.name;
    const cityLat = state.cityData.center.lat;
    const cityLng = state.cityData.center.lng;
    saveMidRound(state.runState, gameState, cityName, cityLat, cityLng);
    setSavedRun({ ...state.runState, midRoundSave: { gameState, cityName, cityLat, cityLng } });
    setState({ screen: 'menu' });
  }, [state]);

  /** Back to menu from practice mode game */
  const handlePracticeBack = useCallback(() => {
    setState({ screen: 'menu' });
  }, []);

  /** Called when the player clicks BEGIN MISSION on the briefing screen. */
  const handleBeginMission = useCallback(() => {
    if (state.screen !== 'mission-briefing') return;
    setState({ screen: 'game', cityData: state.cityData, runState: state.runState });
  }, [state]);

  return (
    <>
      {state.screen === 'menu' && (
        <MainMenu
          onNewRun={handleNewRun}
          onContinueRun={handleContinueRun}
          onPractice={handlePractice}
          onOpenCards={() => setState({ screen: 'cards-reference' })}
          onOpenPassives={() => setState({ screen: 'passives-reference' })}
          onOpenAgents={() => setState({ screen: 'agents-reference' })}
          onOpenStyleguide={() => setState({ screen: 'styleguide' })}
          onOpenTutorial={() => setState({ screen: 'tutorial' })}
          savedRun={savedRun}
        />
      )}

      {state.screen === 'cards-reference' && (
        <CardsReferenceScreen onBack={() => setState({ screen: 'menu' })} />
      )}

      {state.screen === 'passives-reference' && (
        <PassivesReferenceScreen onBack={() => setState({ screen: 'menu' })} />
      )}

      {state.screen === 'agents-reference' && (
        <AgentsReferenceScreen onBack={() => setState({ screen: 'menu' })} />
      )}

      {state.screen === 'styleguide' && (
        <StyleguideScreen onBack={() => setState({ screen: 'menu' })} />
      )}

      {state.screen === 'tutorial' && (
        <TutorialScreen onBack={() => setState({ screen: 'menu' })} />
      )}

      {state.screen === 'build-select' && (
        <BuildSelectScreen
          onSelect={handleBuildSelect}
          onBack={() => setState({ screen: 'menu' })}
        />
      )}

      {state.screen === 'deck-draft' && (
        <DeckDraftScreen
          onComplete={handleDeckDraftComplete}
          isLoading={false}
          loadingStage={''}
          loadingProgress={0}
        />
      )}

      {state.screen === 'practice-search' && (
        <CitySearch onCitySelected={handlePracticeCitySelected} onBack={() => setState({ screen: 'menu' })} />
      )}

      {state.screen === 'loading' && (
        <LoadingScreen
          cityName={state.cityName}
          stage={state.stage}
          progress={state.progress}
          round={state.runState?.round ?? null}
        />
      )}

      {state.screen === 'mission-briefing' && (
        <MissionBriefingScreen
          cityName={state.cityData.name}
          displayName={CURATED_CITIES.find(c => c.name === state.cityData.name)?.displayName}
          round={state.runState?.round ?? null}
          onBegin={handleBeginMission}
        />
      )}

      {state.screen === 'game' && (
        <GameScreen
          cityData={state.cityData}
          onBack={state.runState ? undefined : handlePracticeBack}
          onRoundComplete={state.runState ? handleRoundComplete : undefined}
          onSaveAndExit={state.runState ? handleSaveAndExit : undefined}
          runState={state.runState}
        />
      )}

      {state.screen === 'passive-draft-start' && (
        <PassiveDraftScreen
          options={state.options}
          ownedPassives={state.runState.passives.map(k => PASSIVE_TEMPLATES[k]).filter(Boolean)}
          onPick={handleStartPassivePicked}
          runStart
        />
      )}

      {state.screen === 'kill-summary' && (
        <KillSummaryScreen
          runState={state.runState}
          killMarks={state.killMarks}
          passphraseMarks={state.passphraseMarks}
          collectedItems={state.collectedItems}
          round={state.round}
          onContinue={handleKillSummaryContinue}
        />
      )}

      {state.screen === 'passive-draft-round' && (
        <PassiveDraftScreen
          options={state.options}
          ownedPassives={state.runState.passives.map(k => PASSIVE_TEMPLATES[k]).filter(Boolean)}
          onPick={handleRoundPassivePicked}
          runStart={false}
          round={state.runState.round}
        />
      )}

      {state.screen === 'run-map' && (
        <RunMapScreen
          runState={state.runState}
          onNodeSelected={handleMapNodeSelected}
          onEndRun={handleEndRun}
        />
      )}

      {state.screen === 'extraction-reward' && (
        <ExtractionRewardScreen
          runState={state.runState}
          onPick={handleExtractionRewardPicked}
          onSkip={handleExtractionRewardSkipped}
        />
      )}

      {state.screen === 'hq' && (
        <HQScreen
          runState={state.runState}
          onDeploy={handleDeploy}
          onEndRun={handleEndRun}
          shopEnabled={state.shopEnabled}
        />
      )}

      {/* Black-screen safety net: if state.screen ever ends up at a value
          that none of the branches above match, render a recovery panel
          instead of an empty page. Previously a routing bug could leave
          the player staring at nothing with no way back to the menu. */}
      <UnhandledScreenFallback
        state={state}
        onReturnToMenu={() => setState({ screen: 'menu' })}
        onReturnToMap={() => {
          if ('runState' in state && state.runState) {
            setState({ screen: 'run-map', runState: state.runState });
          } else {
            setState({ screen: 'menu' });
          }
        }}
      />

      <Scanlines />
    </>
  );
}

/**
 * Last-ditch render for screen values that don't match any of the App's
 * conditional render blocks. Drops a visible recovery UI so the user can
 * always get back to the menu / their run. Logs the offending state to
 * the console so the bug can be diagnosed.
 */
function UnhandledScreenFallback({
  state,
  onReturnToMenu,
  onReturnToMap,
}: {
  state: AppState;
  onReturnToMenu: () => void;
  onReturnToMap: () => void;
}) {
  // Whitelist of all known screens — keep in sync with the AppState union
  // and the conditional render blocks above. If a new screen is added but
  // missed in either of those lists, the fallback fires and we see the
  // broken value here.
  const knownScreens = new Set<string>([
    'menu', 'build-select', 'deck-draft', 'practice-search', 'loading',
    'mission-briefing', 'game', 'passive-draft-start', 'passive-draft-round',
    'kill-summary', 'run-map', 'extraction-reward', 'hq',
    'cards-reference', 'passives-reference', 'agents-reference',
    'styleguide', 'tutorial',
  ]);
  if (knownScreens.has(state.screen)) return null;

  // eslint-disable-next-line no-console
  console.error('[UnhandledScreenFallback] Unknown screen value:', state.screen, state);

  return (
    <div style={{
      position: 'fixed',
      inset: 0,
      background: 'rgba(8, 12, 20, 0.98)',
      color: '#dce6f0',
      fontFamily: 'monospace',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
      zIndex: 99999,
    }}>
      <div style={{ fontSize: '14px', color: '#ff8866', letterSpacing: '4px' }}>
        UNRECOGNIZED STATE
      </div>
      <div style={{ fontSize: '11px', color: '#8899aa', maxWidth: '480px', textAlign: 'center' }}>
        The game wandered into a screen state that isn't wired up to a view.
        This is a bug — please report it. You can recover from here without
        losing your run.
      </div>
      <div style={{ fontSize: '10px', color: '#5a6a7a' }}>
        screen = "{String(state.screen)}"
      </div>
      <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
        <button
          onClick={onReturnToMap}
          style={{
            background: 'rgba(68, 204, 136, 0.08)',
            border: '1px solid rgba(68, 204, 136, 0.5)',
            color: '#44cc88',
            padding: '10px 20px',
            fontSize: '11px',
            letterSpacing: '2px',
            cursor: 'pointer',
            fontFamily: 'monospace',
            borderRadius: '4px',
          }}
        >
          RETURN TO RUN PATH
        </button>
        <button
          onClick={onReturnToMenu}
          style={{
            background: 'rgba(255, 80, 80, 0.06)',
            border: '1px solid rgba(255, 80, 80, 0.5)',
            color: '#ff8888',
            padding: '10px 20px',
            fontSize: '11px',
            letterSpacing: '2px',
            cursor: 'pointer',
            fontFamily: 'monospace',
            borderRadius: '4px',
          }}
        >
          BACK TO MENU
        </button>
      </div>
    </div>
  );
}

export default App;
